<template>
    <div class="wrapper">
        <div
            v-if="!timelineIsComplete"
            class="header"
        >
            <div
                class="title"
            >
                <h2>Трек развития</h2>
                <TimelineHeaderTooltip
                    v-if="getPageContent && getPageContent['timeline_tooltip']"
                    :html="getPageContent['timeline_tooltip']"
                />
            </div>
            <TimelineControls
                v-if="startOfInterval"
                :start-of-interval="startOfInterval"
                :months-to-show="monthsToShow"
                :has-prev="hasPrev"
                :has-next="hasNext"
                @prev="prev"
                @next="next"
            />
        </div>
        <div
            class="timeline"
            :class="{ empty: timelineIsEmpty, complete: timelineIsComplete }"
        >
            <template v-if="timelineIsEmpty">
                <svg class="timeline-arrow" width="367" height="129">
                    <use xlink:href="#arrow-track-empty"/>
                </svg>
                <div class="timeline-evolution">
                    <svg class="timeline-evolution-icon" width="80" height="80">
                        <use xlink:href="#evolution"/>
                    </svg>
                    <h5 class="timeline-evolution-heading">Постройте план развития</h5>
                </div>
            </template>
            <template v-else-if="timelineIsComplete">
                <video
                    v-if="showConfetti"
                    class="timeline-complete-confetti" autoplay>
                    <source src="../../../../../src/assets/videos/confetto.webm" type="video/webm" />
                </video>
                <div class="timeline-complete-details">
                    <h5 class="timeline-complete-heading">Поздравляем!</h5>
                    <p class="timeline-complete-text">Вы завершили обучение для достижения поставленной цели и поднялись на вершину, выполнив все
                        задания.</p>
                    <p class="timeline-complete-text">Если вы хотите продолжить обучение, добавьте в трек новые цели или образовательные программы.</p>
                    <router-link
                        :to="{ name: 'profession-list' }"
                        class="timeline-complete-button"
                    >
                        Продолжить обучение
                    </router-link>
                </div>
                <svg class="timeline-complete-bg" width="1225" height="442">
                    <use xlink:href="#track-final"/>
                </svg>
            </template>
            <template v-else>
                <TimelineCompetences
                    :start-of-interval="startOfInterval"
                    :months-to-show="monthsToShow"
                />
                <div
                    class="columns"
                    :style="columnsStyle"
                    ref="columns"
                >
                    <div class="content">
                        <TimelineProgramsDesktop
                            v-if="startOfInterval !== null"
                            :months-offset="monthsOffset"
                            :months-to-show="monthsToShow"
                            :items="programs"
                            :selected-program-id="selectedProgramId"
                            :now-offset="nowOffset"
                            @select="$emit('select', $event)"
                        />
                    </div>
                </div>
                <div class="flags">
                    <TimelineUser
                        v-if="nowOffset"
                        :now-offset="nowOffset"
                        :months-offset="monthsOffset"
                        :months-to-show="monthsToShow"
                    />
                    <TimelineFinish
                        v-if="finishFlagOffset"
                        :flag-offset="finishFlagOffset"
                        :months-offset="monthsOffset"
                        :months-to-show="monthsToShow"
                        :date="finishFlagDate"
                    />
                </div>
                <TimelineAxis
                    :start-of-interval="startOfInterval"
                    :months-to-show="monthsToShow"
                />
            </template>
        </div>
        <TimelineLegend
            v-if="!timelineIsEmpty && !timelineIsComplete"
            class="legend"
        />
    </div>
</template>

<script>
import {mapGetters} from 'vuex';
import moment from 'moment';
import TimelineProgramsDesktop from './TimelineProgramsDesktop';
import TimelineAxis from './Axis';
import TimelineControls from './Controls';
import TimelineLegend from './Legend';
import TimelineCompetences from './TimelineCompetences';
import TimelineFinish from './Finish';
import TimelineUser from './User';
import TimelineHeaderTooltip from './HeaderTooltip';
import {
    calcFullMonthsBetween,
    calcOffsetFromStartOfMonth,
    calcOffsetToEndOfMonth,
} from '../../../../tools/moment-date';

const getters = [
    'getTrackStatus',
    'getTimelinePrograms',
    'getLastDateOfTrack',
    'getEndOfTrack',
    'getStartOfTrack',
    'getCompetenceLevels',
    'getCompetences',
    'getSoftCompetences',
    'getHardCompetences',
    'getPageContent',
];

export default {
    name: 'TimelineDesktop',
    components: {
        TimelineProgramsDesktop,
        TimelineAxis,
        TimelineControls,
        TimelineLegend,
        TimelineCompetences,
        TimelineFinish,
        TimelineUser,
        TimelineHeaderTooltip,
    },

    props: {
        selectedProgramId: {
            type: [String, Number],
            default: null,
        },
    },

    data: () => ({
        monthsOffset: 0,
        breakpoint: 1600,
        isSmall: false,
        showConfetti: false,
    }),
    computed: {
        ...mapGetters('accountTrack', getters),
        finishFlagOffset() {
            let fullMonthsOffset = calcFullMonthsBetween(this.startOfInterval, this.getLastDateOfTrack) + 1 + this.monthsOffset;
            let lastMonthOffset = calcOffsetFromStartOfMonth(this.getLastDateOfTrack);
            let flagOffset = Math.max(fullMonthsOffset + lastMonthOffset, this.nowOffset);
            if (flagOffset - this.nowOffset < 0.15) {
                flagOffset += 0.15;
            }
            return flagOffset;
        },
        nowOffset() {
            let now = moment();
            let fullMonthsOffset = calcFullMonthsBetween(this.getStartOfTrack, now) + 1;
            let lastMonthOffset = calcOffsetFromStartOfMonth(now);
            return fullMonthsOffset + lastMonthOffset;
        },
        maxMonthOffset() {
            return this.getStartOfTrack !== null && this.getEndOfTrack !== null
                ? calcFullMonthsBetween(this.getStartOfTrack, moment.max(this.getEndOfTrack, moment())) + 1
                : null;
        },
        startOfInterval() {
            return this.getStartOfTrack
                ? this.getStartOfTrack
                    .clone()
                    .add(this.monthsOffset, 'months')
                : null;
        },
        programs() {
            return this.getTimelinePrograms.map(v => {
                let modules = v.modules && v.modules.length > 0
                    ? v.modules.map(m => ({
                        ...m,
                        ...this.convertDatesToOffsets(m),
                    }))
                    : [];
                return {
                    ...v,
                    modules: modules,
                    ...this.convertDatesToOffsets(v),
                };
            });
        },
        monthsToShow() {
            if (this.$store.state.geometry.width <= 1024) {
                return 3;
            }
            if (this.$store.state.geometry.width <= 1279) {
                return 5;
            }
            if (this.$store.state.geometry.width <= 1440) {
                return 4;
            }
            if (this.$store.state.geometry.width <= 1920) {
                return 6;
            }
            return 6;
        },
        minBlockSize() {
            return 7;
        },
        hasPrev() {
            return this.monthsOffset > 0;
        },
        hasNext() {
            return this.monthsOffset + this.monthsToShow < this.maxMonthOffset;
        },
        columnsStyle() {
            return {
                'background-image': `repeating-linear-gradient(
                    to right,
                    rgba(0, 0, 0, 0) 0px,
                    rgba(0, 0, 0, 0) 1px,
                    #ccd2d9 1px,
                    #ccd2d9 2px,
                    rgba(0, 0, 0, 0) 2px,
                    rgba(0, 0, 0, 0) calc((100% - 60px) / ${this.monthsToShow} )
                )`,
            };
        },
        finishFlagDate() {
            return moment.max(moment(), this.getLastDateOfTrack);
        },
        timelineIsEmpty() {
            return !this.getTimelinePrograms.length;
        },
        timelineIsComplete() {
            if (this.getCompetences.length === 0) {
                return false;
            }
            for (const competence of this.getSoftCompetences) {
                if (!this.isLevelReached(competence.requiredLevel, competence.competenceLevel)) {
                    return false;
                }
            }
            for (const competence of this.getHardCompetences) {
                if (!competence.userAcknowledgment) {
                    return false;
                }
            }
            return !this.timelineIsEmpty && true;
        },
    },
    watch: {
        getStartOfTrack() {
            this.initMonthOffset();
        },
        startOfInterval() {
            this.resetScroll();
        },
        timelineIsComplete: {
            deep: true,
            immediate: true,
            handler() {
                if (this.timelineIsComplete && localStorage.getItem('timelineCompleteConfetti') !== 'true') {
                    localStorage.setItem('timelineCompleteConfetti', 'true');
                    this.showConfetti = true;
                }
            },
        },
    },
    async mounted() {
        this.initMonthOffset();
        this.resetScroll();
    },
    methods: {
        prev() {
            if (this.hasPrev) {
                this.monthsOffset -= this.monthsToShow;
            }
        },
        next() {
            if (this.hasNext) {
                this.monthsOffset += this.monthsToShow;
            }
        },
        initMonthOffset() {
            if (this.getStartOfTrack) {
                this.monthsOffset = Math.max(calcFullMonthsBetween(this.getStartOfTrack, moment()), 0);
            }
        },
        resetScroll() {
            this.$nextTick(() => {
                if (this.$refs.columns) {
                    this.$refs.columns.scrollTop = 500;
                }
            });
        },
        isLevelReached(required, current) {
            const competenceLevelsPlain = {};
            for (const [index, level] of this.getCompetenceLevels.entries()) {
                competenceLevelsPlain[level.key] = index;
            }
            return current && required && competenceLevelsPlain[current] <= competenceLevelsPlain[required];
        },
        convertDatesToOffsets(v) {
            let firstMonthLength = v.moment.start.daysInMonth();

            let fullMonthsLength = calcFullMonthsBetween(v.moment.start, v.moment.end);

            let afterMonthStart = calcOffsetFromStartOfMonth(v.moment.start);
            let beforeMonthEnd = calcOffsetToEndOfMonth(v.moment.end);

            let size;
            if (fullMonthsLength === -1) {
                if (v.moment.start.isSame(v.moment.end, 'months')) {
                    size = 1 - afterMonthStart - beforeMonthEnd;
                } else {
                    size = 2 - afterMonthStart - beforeMonthEnd;
                }
            } else {
                size = fullMonthsLength + (1 - afterMonthStart) + (1 - beforeMonthEnd);
            }

            size = Math.max(this.minBlockSize / firstMonthLength, size) - 0.025;

            let offset = calcFullMonthsBetween(this.getStartOfTrack, v.moment.start) + 1 + afterMonthStart + 0.0125;
            return {
                size,
                offset,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/rem';

.wrapper {
    border: 1px solid #D2D5D9;
    border-bottom: none;

    @include on-md {
        border-bottom: 1px solid #D2D5D9;
    }

    @include on-mobile {
        display: none;
    }
}

.header {
    user-select: none;
    height: 78px;
    background: white;
    border-radius: 4px 4px 0 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;


    .title {
        width: 200px;
        display: flex;
        align-items: center;

        h2 {
            margin: 0;
            font-style: normal;
            font-weight: 600;
            font-size: 22px;
            line-height: 30px;
        }
    }
}

.timeline {
    background-color: #F5F9FC;
    background-image: url("../../../../assets/images/account-track/track-background.svg");
    background-repeat: no-repeat;
    background-size: cover;
    background-position: right;
    display: flex;
    flex-direction: column;
    min-height: 432px;

    position: relative;

    &.empty {
        min-height: 55vh;
        background-position: top left;
        display: flex;
        justify-content: center;
        overflow: hidden;
    }

    &.complete {
        background: #F5F9FC;
        min-height: 542px;
        overflow: hidden;
    }
}

.timeline-arrow {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translateX(-150%) translateY(-25%);
}

.timeline-evolution {
    margin: auto;
}

.timeline-evolution-icon {
    display: block;
    margin: auto;
}

.timeline-evolution-heading {
    margin: 24px 0 0;
    font-weight: 600;
    font-size: 30px;
    line-height: 38px;
    color: #98A0A9;
}

.timeline-complete-confetti {
    width: 100%;
    height: auto;
    max-height: 100%;
}

.timeline-complete-bg {
    position: absolute;
    bottom: 0;
    width: 100%;
    @media (max-width: 2199px) {
        height: auto;
    }
    @media (min-width: 2200px) {
        bottom: -70px;
    }
}

.timeline-complete-details {
    z-index: 1;
    position: absolute;
    top: 48px;
    left: 80px;
    max-width: 510px;
    text-align: center;
}

.timeline-complete-heading {
    margin: 0 0 16px;
    font-weight: 600;
    font-size: 28px;
    line-height: 36px;
}

.timeline-complete-text {
    margin: 0 0 8px;
    font-weight: normal;
    font-size: 18px;
    line-height: 26px;
}

.timeline-complete-button {
    display: inline-block;
    padding: 11px 43px;
    margin-top: 24px;
    background: #4663AB;
    color: #fff;
    font-weight: bold;
    font-size: 18px;
    line-height: 26px;
    border-radius: 4px;
}

.flags {
    position: relative;
    margin: 0 32px;
}

.columns {
    display: flex;
    width: 100%;
    justify-content: flex-end;
    position: relative;
    min-height: 432px;
    max-height: 432px;
    height: 432px;
    overflow-y: scroll;
    overflow-x: hidden;

    &::-webkit-scrollbar {
        height: auto;
        width: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background: #E1E1E1;
        border-radius: 0.3rem;
    }

    &::-webkit-scrollbar-track {
        background: none;
    }

    .prev,
    .next {
        width: 32px;
        cursor: pointer;

        &.disabled {
            opacity: 0;
            cursor: default;
        }
    }

    background-position: 30px bottom;
}

.months {
    display: flex;
    width: 100%;
    justify-content: space-between;

    padding: 0 32px;

    .column {
        border: 0;
        flex: 0;
        width: 1px;
        display: flex;
        justify-content: center;
        align-items: flex-end;

        .label {
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            font-size: 12px;
            line-height: 16px;
            color: #98A0A9;
        }
    }

    min-height: 32px;
}

.content {
    width: 100%;
    /*display: flex;*/
    /*flex-direction: column;*/
    /*justify-content: space-between;*/
    position: relative;
    margin: 0 26px 0 32px;
}


.legend {
    background: white;
    padding: 24px 24px 20px;
}
</style>
